import { useState, useEffect } from 'react'
import { useEditorStore } from '../../store/editor.store'
import { redirectsApi, ApiRedirect, webhooksApi, ApiWebhook, inboundWebhooksApi, ApiInboundWebhook, apiKeysApi, ApiKey, workspacesApi, ApiWorkspace, ApiWorkspaceMember, api, sitesApi, BASE } from '../../utils/api'
import { DESIGN_STYLES } from '../../data/designStyles'
import { SaveAsTemplateModal } from '../SaveAsTemplateModal'

interface DomainVerifyResult {
  status: 'ok' | 'pending' | 'not-configured'
  domain: string
  cname: string | null
  expected: string
  verified: boolean
}

function DomainVerifier({ siteId, domain }: { siteId: string | null; domain: string }) {
  const [result, setResult] = useState<DomainVerifyResult | null>(null)
  const [loading, setLoading] = useState(false)

  const check = async () => {
    if (!siteId) return
    setLoading(true)
    try {
      const r = await sitesApi.verifyDomain(siteId)
      setResult(r)
    } catch {
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  const statusBadge = result
    ? result.status === 'ok'
      ? { color: '#10b981', bg: '#ecfdf5', icon: '✓', text: 'Домен подключён и работает' }
      : result.status === 'pending'
        ? { color: '#f59e0b', bg: '#fffbeb', icon: '⏳', text: 'DNS ещё распространяется (до 48 ч)' }
        : { color: '#ef4444', bg: '#fef2f2', icon: '✗', text: 'CNAME не настроен' }
    : null

  return (
    <div style={{ marginTop: '8px' }}>
      <button
        onClick={check}
        disabled={loading || !siteId}
        style={{ padding: '6px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: 'var(--text-primary)' }}
      >
        {loading ? '⟳ Проверяю...' : '🔍 Проверить DNS'}
      </button>
      {statusBadge && (
        <div style={{ marginTop: '8px', padding: '8px 12px', background: statusBadge.bg, borderRadius: '6px', fontSize: '12px' }}>
          <span style={{ color: statusBadge.color, fontWeight: 600 }}>{statusBadge.icon} {statusBadge.text}</span>
          {result?.cname && <div style={{ color: '#888', marginTop: '4px' }}>Текущий CNAME: {result.cname}</div>}
          {result?.status !== 'ok' && (
            <div style={{ color: '#888', marginTop: '4px' }}>
              Добавьте CNAME-запись у вашего регистратора:<br />
              <code style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '2px 6px', borderRadius: '3px', display: 'inline-block', marginTop: '4px' }}>
                {domain} → {result?.expected}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RedirectsSection({ siteId }: { siteId: string | null }) {
  const [redirects, setRedirects] = useState<ApiRedirect[]>([])
  const [form, setForm] = useState({ from: '', to: '', permanent: false })
  const [saving, setSaving] = useState(false)

  const load = () => {
    if (!siteId) return
    redirectsApi.list(siteId).then(setRedirects).catch(() => {})
  }

  useEffect(() => { load() }, [siteId])

  const handleCreate = async () => {
    if (!siteId || !form.from || !form.to) return
    setSaving(true)
    try {
      await redirectsApi.create(siteId, form)
      setForm({ from: '', to: '', permanent: false })
      load()
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const handleToggle = async (r: ApiRedirect) => {
    if (!siteId) return
    await redirectsApi.update(siteId, r.id, { active: !r.active }).catch(() => null)
    load()
  }

  const handleDelete = async (r: ApiRedirect) => {
    if (!siteId || !confirm(`Удалить редирект ${r.from}?`)) return
    await redirectsApi.delete(siteId, r.id).catch(() => null)
    load()
  }

  if (!siteId) return <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Сохраните сайт чтобы управлять редиректами</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Откуда</label>
          <input className="prop-input" placeholder="/old-page" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Куда</label>
          <input className="prop-input" placeholder="/new-page или https://..." value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} />
        </div>
        <div style={{ flexShrink: 0 }}>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>301</label>
          <input type="checkbox" checked={form.permanent} onChange={e => setForm(f => ({ ...f, permanent: e.target.checked }))} style={{ width: '16px', height: '16px' }} />
        </div>
        <button onClick={handleCreate} disabled={saving || !form.from || !form.to}
          style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.from || !form.to ? 0.6 : 1, flexShrink: 0 }}>
          +
        </button>
      </div>

      {redirects.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '12px' }}>Нет редиректов</div>}
      {redirects.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'var(--bg-3)', borderRadius: '8px', opacity: r.active ? 1 : 0.5 }}>
          <div style={{ flex: 1, fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ color: 'var(--text-muted)' }}>{r.from}</span> → {r.to}
          </div>
          <span style={{ fontSize: '10px', background: r.permanent ? '#5b6af022' : '#f5f5f5', color: r.permanent ? 'var(--accent)' : 'var(--text-muted)', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>
            {r.permanent ? '301' : '302'}
          </span>
          <button onClick={() => handleToggle(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)', flexShrink: 0 }} title={r.active ? 'Отключить' : 'Включить'}>
            {r.active ? '●' : '○'}
          </button>
          <button onClick={() => handleDelete(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#ef4444', flexShrink: 0 }}>×</button>
        </div>
      ))}
    </div>
  )
}

function WorkspaceSection({ userPlan }: { userPlan: string }) {
  const [memberships, setMemberships] = useState<ApiWorkspaceMember[]>([])
  const [selected, setSelected] = useState<(ApiWorkspace & { members: ApiWorkspaceMember[] }) | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'EDITOR' | 'VIEWER'>('EDITOR')
  const [createName, setCreateName] = useState('')
  const [createSlug, setCreateSlug] = useState('')
  const [saving, setSaving] = useState(false)
  const [wl, setWl] = useState<Partial<ApiWorkspace>>({})

  useEffect(() => {
    workspacesApi.list().then(setMemberships).catch(() => {})
  }, [])

  const loadWorkspace = (id: string) => {
    workspacesApi.get(id).then(ws => {
      setSelected(ws)
      setWl({
        whitelabelEnabled: ws.whitelabelEnabled,
        whitelabelName: ws.whitelabelName ?? '',
        whitelabelLogo: ws.whitelabelLogo ?? '',
        whitelabelColor: ws.whitelabelColor ?? '#5b6af0',
        whitelabelDomain: ws.whitelabelDomain ?? '',
      })
    }).catch(() => {})
  }

  const handleCreate = async () => {
    if (!createName.trim() || !createSlug.trim()) return
    setSaving(true)
    try {
      await workspacesApi.create(createName.trim(), createSlug.trim().toLowerCase().replace(/\s+/g, '-'))
      const memberships = await workspacesApi.list()
      setMemberships(memberships)
      setCreateName(''); setCreateSlug('')
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const handleSaveWl = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await workspacesApi.update(selected.id, wl)
      loadWorkspace(selected.id)
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const handleInvite = async () => {
    if (!selected || !inviteEmail.trim()) return
    setSaving(true)
    try {
      await workspacesApi.invite(selected.id, inviteEmail.trim(), inviteRole)
      setInviteEmail('')
      loadWorkspace(selected.id)
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const handleRemoveMember = async (userId: string) => {
    if (!selected || !confirm('Удалить участника?')) return
    await workspacesApi.removeMember(selected.id, userId).catch(() => null)
    loadWorkspace(selected.id)
  }

  if (selected) {
    const myRole = selected.members.find(m => m.user)?.role ?? 'VIEWER'
    const canAdmin = ['OWNER', 'ADMIN'].includes(myRole)

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', padding: 0 }}>← Назад</button>
          <span style={{ fontSize: '13px', fontWeight: 700 }}>{selected.name}</span>
          <span style={{ fontSize: '10px', background: 'var(--bg-3)', color: 'var(--text-muted)', padding: '2px 8px', borderRadius: '10px' }}>{myRole}</span>
        </div>

        {/* White-label settings */}
        {canAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', background: 'var(--bg-3)', borderRadius: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>White Label</div>
              {!['AGENCY', 'ENTERPRISE'].includes(userPlan) && (
                <span style={{ fontSize: '10px', fontWeight: 700, background: '#f59e0b22', color: '#f59e0b', padding: '2px 8px', borderRadius: '8px' }}>Agency+</span>
              )}
            </div>
            {['AGENCY', 'ENTERPRISE'].includes(userPlan) ? (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={wl.whitelabelEnabled ?? false} onChange={e => setWl(w => ({ ...w, whitelabelEnabled: e.target.checked }))} style={{ width: '16px', height: '16px', accentColor: 'var(--accent)' }} />
                  Включить White Label
                </label>
                {wl.whitelabelEnabled && (
                  <>
                    <div>
                      <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Название платформы</label>
                      <input className="prop-input" placeholder="Название вашего SaaS" value={wl.whitelabelName ?? ''} onChange={e => setWl(w => ({ ...w, whitelabelName: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>URL логотипа</label>
                      <input className="prop-input" placeholder="https://example.com/logo.png" value={wl.whitelabelLogo ?? ''} onChange={e => setWl(w => ({ ...w, whitelabelLogo: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Основной цвет</label>
                        <input className="prop-input" placeholder="#5b6af0" value={wl.whitelabelColor ?? ''} onChange={e => setWl(w => ({ ...w, whitelabelColor: e.target.value }))} />
                      </div>
                      {wl.whitelabelColor && (
                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: wl.whitelabelColor, border: '1px solid var(--border)', flexShrink: 0 }} />
                      )}
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Кастомный домен платформы</label>
                      <input className="prop-input" placeholder="builder.mycompany.com" value={wl.whitelabelDomain ?? ''} onChange={e => setWl(w => ({ ...w, whitelabelDomain: e.target.value }))} />
                    </div>
                  </>
                )}
                <button onClick={handleSaveWl} disabled={saving} style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1, alignSelf: 'flex-start' }}>
                  Сохранить
                </button>
              </>
            ) : (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                White Label позволяет использовать собственный бренд, логотип и домен для платформы. Доступно на тарифе <strong style={{ color: '#f59e0b' }}>Agency</strong>.
              </div>
            )}
          </div>
        )}

        {/* Members */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Участники ({selected.members.length})</div>
          {selected.members.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'var(--bg-3)', borderRadius: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
                {(m.user?.name ?? m.user?.email ?? '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.user?.name ?? m.user?.email}</div>
                {m.user?.name && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{m.user.email}</div>}
              </div>
              <span style={{ fontSize: '10px', background: m.role === 'OWNER' ? '#f59e0b22' : 'var(--bg-2)', color: m.role === 'OWNER' ? '#f59e0b' : 'var(--text-muted)', padding: '2px 8px', borderRadius: '10px', flexShrink: 0 }}>{m.role}</span>
              {canAdmin && m.role !== 'OWNER' && (
                <button onClick={() => handleRemoveMember(m.userId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '13px', flexShrink: 0 }}>×</button>
              )}
            </div>
          ))}
        </div>

        {/* Invite */}
        {canAdmin && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
            <input className="prop-input" placeholder="email участника" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={{ flex: 1 }} />
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'ADMIN' | 'EDITOR' | 'VIEWER')} style={{ padding: '7px 8px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '12px' }}>
              <option value="EDITOR">Editor</option>
              <option value="ADMIN">Admin</option>
              <option value="VIEWER">Viewer</option>
            </select>
            <button onClick={handleInvite} disabled={saving || !inviteEmail.trim()} style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !inviteEmail.trim() ? 0.6 : 1, flexShrink: 0 }}>
              Пригласить
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {memberships.length === 0 && (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px' }}>Нет рабочих пространств</div>
      )}
      {memberships.map(m => (
        <button
          key={m.id}
          onClick={() => loadWorkspace(m.workspaceId)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', width: '100%' }}
        >
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '16px', fontWeight: 700, flexShrink: 0 }}>
            {m.workspace?.name[0].toUpperCase() ?? 'W'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{m.workspace?.name}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {m.workspace?._count?.members ?? 0} участников · {m.workspace?._count?.sites ?? 0} сайтов · {m.role}
            </div>
          </div>
          <span style={{ fontSize: '14px', color: 'var(--text-muted)', flexShrink: 0 }}>›</span>
        </button>
      ))}

      <div style={{ paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Создать новое</div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input className="prop-input" placeholder="Название" value={createName} onChange={e => { setCreateName(e.target.value); setCreateSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) }} style={{ flex: 1 }} />
          <input className="prop-input" placeholder="slug" value={createSlug} onChange={e => setCreateSlug(e.target.value)} style={{ width: '100px', fontFamily: 'monospace', fontSize: '11px' }} />
          <button onClick={handleCreate} disabled={saving || !createName.trim() || !createSlug.trim()} style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !createName.trim() || !createSlug.trim() ? 0.6 : 1, flexShrink: 0 }}>
            +
          </button>
        </div>
      </div>
    </div>
  )
}

const ALL_EVENTS = ['form.submit', 'order.created', 'order.status_changed', 'site.published']

function WebhooksSection({ siteId }: { siteId: string | null }) {
  const [hooks, setHooks] = useState<ApiWebhook[]>([])
  const [form, setForm] = useState({ url: '', events: ['form.submit'], secret: '' })
  const [saving, setSaving] = useState(false)

  const load = () => {
    if (!siteId) return
    webhooksApi.list(siteId).then(setHooks).catch(() => {})
  }

  useEffect(() => { load() }, [siteId])

  const toggleEvent = (ev: string) =>
    setForm(f => ({ ...f, events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev] }))

  const handleCreate = async () => {
    if (!siteId || !form.url || form.events.length === 0) return
    setSaving(true)
    try {
      await webhooksApi.create(siteId, { url: form.url, events: form.events, secret: form.secret || undefined })
      setForm({ url: '', events: ['form.submit'], secret: '' })
      load()
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const handleToggle = async (h: ApiWebhook) => {
    if (!siteId) return
    await webhooksApi.update(siteId, h.id, { active: !h.active }).catch(() => null)
    load()
  }

  const handleDelete = async (h: ApiWebhook) => {
    if (!siteId || !confirm(`Удалить вебхук ${h.url}?`)) return
    await webhooksApi.delete(siteId, h.id).catch(() => null)
    load()
  }

  if (!siteId) return <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Сохраните сайт чтобы настроить вебхуки</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: 'var(--bg-3)', borderRadius: '10px' }}>
        <input className="prop-input" placeholder="https://hooks.example.com/bst" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
        <input className="prop-input" placeholder="Secret (необязательно)" value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {ALL_EVENTS.map(ev => (
            <label key={ev} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer', padding: '3px 8px', background: form.events.includes(ev) ? 'var(--accent)22' : 'var(--bg-2)', borderRadius: '5px', border: `1px solid ${form.events.includes(ev) ? 'var(--accent)' : 'var(--border)'}` }}>
              <input type="checkbox" checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)} style={{ display: 'none' }} />
              {ev}
            </label>
          ))}
        </div>
        <button onClick={handleCreate} disabled={saving || !form.url || form.events.length === 0}
          style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.url || form.events.length === 0 ? 0.6 : 1, alignSelf: 'flex-start' }}>
          Добавить вебхук
        </button>
      </div>

      {hooks.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px' }}>Нет вебхуков</div>}
      {hooks.map(h => (
        <div key={h.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', background: 'var(--bg-3)', borderRadius: '8px', opacity: h.active ? 1 : 0.5 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.url}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' }}>
              {h.events.map(ev => (
                <span key={ev} style={{ fontSize: '10px', background: 'var(--accent)22', color: 'var(--accent)', padding: '2px 6px', borderRadius: '4px' }}>{ev}</span>
              ))}
            </div>
          </div>
          <button onClick={() => handleToggle(h)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)', flexShrink: 0 }} title={h.active ? 'Отключить' : 'Включить'}>
            {h.active ? '●' : '○'}
          </button>
          <button onClick={() => handleDelete(h)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#ef4444', flexShrink: 0 }}>×</button>
        </div>
      ))}
    </div>
  )
}

function InboundWebhooksSection({ siteId }: { siteId: string | null }) {
  const [hooks, setHooks] = useState<ApiInboundWebhook[]>([])
  const [form, setForm] = useState({ name: '', action: 'store_submission', collectionSlug: '' })
  const [saving, setSaving] = useState(false)

  const load = () => { if (siteId) inboundWebhooksApi.list(siteId).then(setHooks).catch(() => {}) }
  useEffect(() => { load() }, [siteId])

  const handleCreate = async () => {
    if (!siteId || !form.name.trim()) return
    setSaving(true)
    try {
      await inboundWebhooksApi.create(siteId, {
        name: form.name,
        action: form.action,
        ...(form.action === 'create_cms_item' && form.collectionSlug ? { collectionSlug: form.collectionSlug } : {}),
      })
      setForm({ name: '', action: 'store_submission', collectionSlug: '' })
      load()
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const handleDelete = async (h: ApiInboundWebhook) => {
    if (!siteId || !confirm(`Удалить входящий вебхук «${h.name}»?`)) return
    await inboundWebhooksApi.delete(siteId, h.id).catch(() => null)
    load()
  }

  const baseUrl = window.location.origin

  if (!siteId) return <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Сохраните сайт, чтобы настроить входящие вебхуки</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Получайте данные от Zapier, Make или любого сервиса. Каждый вебхук имеет уникальный URL — отправьте туда POST-запрос с JSON.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: 'var(--bg-3)', borderRadius: '10px' }}>
        <input className="prop-input" placeholder="Название" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <select className="prop-input" value={form.action} onChange={e => setForm(f => ({ ...f, action: e.target.value }))}>
          <option value="store_submission">Сохранить как заявку (Forms)</option>
          <option value="create_cms_item">Создать запись в CMS</option>
        </select>
        {form.action === 'create_cms_item' && (
          <input className="prop-input" placeholder="Slug коллекции CMS" value={form.collectionSlug} onChange={e => setForm(f => ({ ...f, collectionSlug: e.target.value }))} />
        )}
        <button onClick={handleCreate} disabled={saving || !form.name.trim()}
          style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.name.trim() ? 0.6 : 1, alignSelf: 'flex-start' }}>
          Создать вебхук
        </button>
      </div>

      {hooks.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px' }}>Нет входящих вебхуков</div>}
      {hooks.map(h => (
        <div key={h.id} style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px 12px', background: 'var(--bg-3)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ flex: 1, fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{h.name}</div>
            <span style={{ fontSize: '10px', background: 'var(--accent)22', color: 'var(--accent)', padding: '2px 6px', borderRadius: '4px' }}>{h.action === 'store_submission' ? 'заявка' : 'cms'}</span>
            {h.receivedAt && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>✓ {new Date(h.receivedAt).toLocaleString('ru')}</span>}
            <button onClick={() => handleDelete(h)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#ef4444', flexShrink: 0 }}>×</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <code style={{ flex: 1, fontSize: '10px', fontFamily: 'monospace', background: 'var(--bg-2)', padding: '4px 8px', borderRadius: '5px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              POST {baseUrl}/api/inbound/{h.token}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(`${baseUrl}/api/inbound/${h.token}`)}
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer', fontSize: '10px', padding: '3px 7px', color: 'var(--text-muted)', flexShrink: 0 }}
            >
              Копировать
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [form, setForm] = useState({ name: '', expiresAt: '' })
  const [saving, setSaving] = useState(false)
  const [newKey, setNewKey] = useState<string | null>(null)
  const [revokeConfirm, setRevokeConfirm] = useState<ApiKey | null>(null)

  const load = () => { apiKeysApi.list().then(setKeys).catch(() => {}) }

  useEffect(() => { load() }, [])

  const handleCreate = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      const created = await apiKeysApi.create(form.name.trim(), form.expiresAt || undefined)
      setNewKey(created.key)
      setForm({ name: '', expiresAt: '' })
      load()
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const handleRevoke = (k: ApiKey) => {
    setRevokeConfirm(k)
  }

  const confirmRevoke = async () => {
    const k = revokeConfirm
    if (!k) return
    setRevokeConfirm(null)
    await apiKeysApi.revoke(k.id).catch(() => null)
    load()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {newKey && (
        <div style={{ background: '#16a34a11', border: '1px solid #16a34a44', borderRadius: '8px', padding: '10px 12px' }}>
          <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600, marginBottom: '5px' }}>API ключ создан. Сохраните его — он больше не будет показан!</div>
          <div style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all', color: 'var(--text-primary)', background: 'var(--bg-3)', padding: '7px 10px', borderRadius: '6px', userSelect: 'all' }}>{newKey}</div>
          <button onClick={() => setNewKey(null)} style={{ marginTop: '6px', fontSize: '11px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}>Скрыть</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Название</label>
          <input className="prop-input" placeholder="Production key" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div style={{ width: '130px' }}>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Срок действия</label>
          <input type="date" className="prop-input" value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
        </div>
        <button onClick={handleCreate} disabled={saving || !form.name.trim()}
          style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.name.trim() ? 0.6 : 1, flexShrink: 0 }}>
          Создать
        </button>
      </div>

      {keys.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px' }}>Нет API ключей</div>}
      {keys.map(k => (
        <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'var(--bg-3)', borderRadius: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{k.name}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
              <span style={{ fontFamily: 'monospace' }}>{k.key.slice(0, 12)}…</span>
              {k.lastUsed && <span style={{ marginLeft: '8px' }}>Последнее использование: {new Date(k.lastUsed).toLocaleDateString('ru')}</span>}
              {k.expiresAt && <span style={{ marginLeft: '8px', color: new Date(k.expiresAt) < new Date() ? '#ef4444' : 'inherit' }}>Истекает: {new Date(k.expiresAt).toLocaleDateString('ru')}</span>}
            </div>
          </div>
          <button onClick={() => handleRevoke(k)} style={{ background: 'none', cursor: 'pointer', fontSize: '12px', color: '#ef4444', flexShrink: 0, padding: '4px 8px', borderRadius: '6px', border: '1px solid #ef444444' }}>Отозвать</button>
        </div>
      ))}

      {revokeConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '24px 28px', maxWidth: '340px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '16px', fontWeight: 800, marginBottom: '8px', color: '#111' }}>Отозвать ключ?</div>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', lineHeight: 1.5 }}>
              Ключ <strong>«{revokeConfirm.name}»</strong> будет отозван. Это действие необратимо.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRevokeConfirm(null)}
                style={{ padding: '7px 16px', background: '#f3f4f6', color: '#333', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                Отмена
              </button>
              <button
                onClick={confirmRevoke}
                style={{ padding: '7px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
              >
                Отозвать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const GOOGLE_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Montserrat', 'Raleway', 'Nunito', 'Poppins',
  'Lato', 'Source Sans Pro', 'Playfair Display', 'Merriweather', 'Lora',
  'PT Sans', 'PT Serif', 'Oswald', 'Ubuntu', 'Rubik', 'Comfortaa',
]

function SiteTransferSection({ siteId }: { siteId: string }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handle = async () => {
    if (!email.trim() || !confirm(`Передать сайт пользователю ${email}? Вы потеряете доступ владельца.`)) return
    setStatus('loading')
    try {
      await api.post(`/sites/${siteId}/transfer`, { email: email.trim().toLowerCase() })
      setStatus('done')
      localStorage.removeItem('bst-site-id')
      localStorage.removeItem('bst-site-slug')
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Ошибка')
      setStatus('error')
    }
  }

  if (status === 'done') return <div style={{ fontSize: '13px', color: '#15803d', padding: '10px 0' }}>✓ Сайт передан. Перезагрузите страницу.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Передать права владельца другому пользователю BST. После передачи вы потеряете права владельца.</div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          type="email"
          className="prop-input"
          value={email}
          onChange={e => { setEmail(e.target.value); setStatus('idle') }}
          placeholder="email@example.com"
        />
        <button
          onClick={handle}
          disabled={status === 'loading' || !email.trim()}
          style={{ padding: '7px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: !email.trim() ? 0.5 : 1, flexShrink: 0 }}
        >
          {status === 'loading' ? '...' : 'Передать'}
        </button>
      </div>
      {status === 'error' && <div style={{ fontSize: '12px', color: '#ef4444' }}>{errorMsg}</div>}
    </div>
  )
}

function SitePasswordSection({ siteId }: { siteId: string }) {
  const [password, setPassword] = useState('')
  const [status, setStatus] = useState<'idle' | 'saving' | 'ok' | 'error'>('idle')
  const [isProtected, setIsProtected] = useState<boolean | null>(null)

  useEffect(() => {
    api.get<{ sitePasswordProtected: boolean }>(`/sites/${siteId}`)
      .then(s => setIsProtected(!!(s as Record<string, unknown>).sitePasswordProtected))
      .catch(() => {})
  }, [siteId])

  const handleSet = async () => {
    if (!password.trim()) return
    setStatus('saving')
    try {
      await api.patch(`/sites/${siteId}`, { sitePassword: password.trim() })
      setStatus('ok')
      setIsProtected(true)
      setPassword('')
    } catch { setStatus('error') }
  }

  const handleClear = async () => {
    if (!confirm('Снять пароль с сайта?')) return
    setStatus('saving')
    try {
      await api.patch(`/sites/${siteId}`, { sitePassword: '' })
      setStatus('ok')
      setIsProtected(false)
    } catch { setStatus('error') }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {isProtected && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px', color: '#92400e' }}>
          <span>🔒 Сайт защищён паролем</span>
          <button onClick={handleClear} disabled={status === 'saving'} style={{ marginLeft: 'auto', padding: '4px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
            Снять пароль
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          type="password"
          className="prop-input"
          value={password}
          onChange={e => { setPassword(e.target.value); setStatus('idle') }}
          placeholder={isProtected ? 'Новый пароль (для смены)' : 'Введите пароль'}
        />
        <button
          onClick={handleSet}
          disabled={status === 'saving' || !password.trim()}
          style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: !password.trim() ? 0.5 : 1, flexShrink: 0 }}
        >
          {status === 'saving' ? '...' : isProtected ? 'Сменить' : 'Защитить'}
        </button>
      </div>
      {status === 'ok' && <div style={{ fontSize: '12px', color: '#15803d' }}>✓ Сохранено</div>}
      {status === 'error' && <div style={{ fontSize: '12px', color: '#ef4444' }}>Ошибка сохранения</div>}
      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>При входе пользователи увидят форму ввода пароля. Пароль хранится в зашифрованном виде.</div>
    </div>
  )
}

function NewsletterSection({ siteId }: { siteId: string }) {
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    api.get<{ id: string }[]>(`/sites/${siteId}/newsletter/subscribers`)
      .then(subs => setCount(subs.length))
      .catch(() => setCount(0))
  }, [siteId])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
        {count === null ? 'Загрузка...' : `${count} подписчик${count === 1 ? '' : count < 5 ? 'а' : 'ов'}`}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <a
          href={`${(import.meta.env.VITE_API_URL ?? '') + '/api'}/sites/${siteId}/newsletter/export`}
          download
          style={{ padding: '7px 14px', background: 'var(--bg-2)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}
        >
          ↓ Скачать CSV
        </a>
      </div>
      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Подписчики собираются через блок «Форма подписки» на вашем сайте.</div>
    </div>
  )
}

// ── §5: Marketing consent (152-ФЗ ст.9 + ФЗ «О рекламе» ст.18) ──────────────
function MarketingConsentSection() {
  const [agree, setAgree] = useState<boolean | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<{ agreeToMarketing?: boolean }>('/users/me')
      .then(u => setAgree(!!u.agreeToMarketing))
      .catch(() => setAgree(false))
  }, [])

  const toggle = async (val: boolean) => {
    const prev = agree
    setAgree(val)
    setError('')
    try {
      await api.patch('/users/me/marketing', { agree: val })
    } catch {
      setAgree(prev)
      setError('Не удалось сохранить. Попробуйте позже.')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!agree}
          disabled={agree === null}
          onChange={(e) => void toggle(e.target.checked)}
        />
        <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
          Получать новости и специальные предложения BST
        </span>
      </label>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
        Мы храним факт согласия и его отзыв с датой — согласно требованиям 152-ФЗ.
        {error && <span style={{ color: '#ef4444', display: 'block', marginTop: 4 }}>{error}</span>}
      </div>
    </div>
  )
}

// ── §6: Self-service account deletion (152-ФЗ ст.14 ч.1 п.7, ст.21) ─────────
function DeleteAccountSection() {
  const [confirming, setConfirming] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (confirmText !== 'УДАЛИТЬ') return
    setDeleting(true)
    setError('')
    try {
      await api.delete('/users/me')
      localStorage.removeItem('bst-token')
      localStorage.removeItem('bst-refresh')
      localStorage.removeItem('bst-auth')
      window.location.href = '/'
    } catch {
      setError('Не удалось удалить аккаунт. Напишите на hello@mybst.ru.')
    } finally {
      setDeleting(false)
    }
  }

  if (!confirming) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Полное удаление: аккаунт, все сайты, страницы, медиа-файлы, сохранённые блоки и API-ключи будут удалены без возможности восстановления. Записи безопасности деперсонифицируются.
        </div>
        <button
          onClick={() => setConfirming(true)}
          style={{ alignSelf: 'flex-start', padding: '8px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
        >
          Удалить аккаунт
        </button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '8px', fontSize: '12px', color: '#7f1d1d', lineHeight: 1.6 }}>
        Введите <strong>УДАЛИТЬ</strong> заглавными русскими буквами, чтобы подтвердить.
      </div>
      <input
        type="text"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder="УДАЛИТЬ"
        style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: 'var(--bg-2)', color: 'var(--text-primary)' }}
      />
      {error && <div style={{ fontSize: '12px', color: '#dc2626' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => void submit()}
          disabled={confirmText !== 'УДАЛИТЬ' || deleting}
          style={{ padding: '8px 14px', background: confirmText === 'УДАЛИТЬ' && !deleting ? '#dc2626' : '#f3f4f6', color: confirmText === 'УДАЛИТЬ' && !deleting ? '#fff' : '#9ca3af', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: confirmText === 'УДАЛИТЬ' && !deleting ? 'pointer' : 'not-allowed' }}
        >
          {deleting ? 'Удаляю...' : 'Подтвердить удаление'}
        </button>
        <button
          onClick={() => { setConfirming(false); setConfirmText(''); setError('') }}
          disabled={deleting}
          style={{ padding: '8px 14px', background: 'var(--bg-2)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
        >
          Отмена
        </button>
      </div>
    </div>
  )
}

function WeeklyReportsSection() {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  useEffect(() => {
    api.get<{ weeklyReports?: boolean }>('/users/me')
      .then(u => setEnabled(u.weeklyReports ?? true))
      .catch(() => setEnabled(true))
  }, [])

  const toggle = async (val: boolean) => {
    setEnabled(val)
    await api.patch('/users/me/weekly-reports', { enabled: val }).catch(() => null)
  }

  const sendTest = async () => {
    setSending(true)
    try {
      await api.post('/users/me/weekly-reports/send-test', {})
      setSent(true)
      setTimeout(() => setSent(false), 3000)
    } catch {
      // ignore
    } finally {
      setSending(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={enabled ?? true}
          onChange={(e) => void toggle(e.target.checked)}
        />
        <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
          Получать еженедельную статистику по email
        </span>
      </label>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
        Каждый понедельник в 9:00 — просмотры, заявки, заказы, топ страниц.
      </div>
      <button
        onClick={() => void sendTest()}
        disabled={sending}
        style={{ alignSelf: 'flex-start', padding: '6px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: 'var(--text-primary)' }}
      >
        {sending ? '⟳ Отправка...' : sent ? '✓ Отправлено!' : '📧 Тестовый отчёт'}
      </button>
    </div>
  )
}

export function SettingsModal() {
  const { settingsOpen, setSettingsOpen, settings, updateSettings, pages } = useEditorStore()
  const siteId = localStorage.getItem('bst-site-id')
  const [userPlan, setUserPlan] = useState<string>('FREE')
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false)

  useEffect(() => {
    if (!settingsOpen) return
    sitesApi.getPlanUsage().then(u => setUserPlan(u.plan)).catch(() => null)
  }, [settingsOpen])

  if (!settingsOpen) return null

  const totalPages = pages.length
  const totalBlocks = pages.reduce((sum, p) => sum + p.sections.reduce((s, sec) => s + sec.blocks.length, 0), 0)

  const theme: { primaryColor?: string; bgColor?: string; textColor?: string; borderRadius?: number; fontHeading?: string; fontBody?: string; customVars?: string; surface?: string; accentMuted?: string; textMuted?: string; border?: string } =
    (() => { try { return JSON.parse(settings.themeConfig || '{}') } catch { return {} } })()
  const updateTheme = (patch: Record<string, unknown>) => {
    const current: Record<string, unknown> = (() => { try { return JSON.parse(settings.themeConfig || '{}') } catch { return {} } })()
    updateSettings({ themeConfig: JSON.stringify({ ...current, ...patch }) })
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) setSettingsOpen(false) }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>Настройки сайта</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {totalPages} {totalPages === 1 ? 'страница' : 'страниц'} · {totalBlocks} блоков
            </div>
          </div>
          <button
            onClick={() => setSettingsOpen(false)}
            style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '28px' }}>

          {/* Site Identity */}
          <SettingsSection title="Идентичность сайта" icon="🏠">
            <SettingRow label="Название сайта" hint="Используется в заголовке вкладки">
              <input
                type="text"
                className="prop-input"
                value={settings.name}
                placeholder="Мой сайт"
                onChange={(e) => updateSettings({ name: e.target.value })}
              />
            </SettingRow>
            <SettingRow label="Favicon" hint="Иконка во вкладке браузера (ICO, PNG 32×32)">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={settings.favicon}
                  placeholder="https://example.com/favicon.ico"
                  onChange={(e) => updateSettings({ favicon: e.target.value })}
                />
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', padding: '5px 8px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}
                  title="Загрузить с устройства"
                >
                  📁
                  <input
                    type="file"
                    accept="image/*,.ico"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const fd = new FormData()
                      fd.append('file', file)
                      const token = localStorage.getItem('bst-token') ?? ''
                      const res = await fetch(`${BASE}/media/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd })
                      if (res.ok) { const data = await res.json() as { url: string }; updateSettings({ favicon: data.url }) }
                    }}
                  />
                </label>
                {settings.favicon && (
                  <img
                    src={settings.favicon}
                    alt="favicon"
                    style={{ width: '24px', height: '24px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border)', background: '#fff', padding: '2px', flexShrink: 0 }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                )}
              </div>
            </SettingRow>
          </SettingsSection>

          {/* Design & Typography */}
          <SettingsSection title="Дизайн и типографика" icon="🎨">
            <SettingRow label="Акцентный цвет" hint="Основной цвет кнопок, ссылок и элементов интерфейса">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={theme.primaryColor || '#5b6af0'}
                  onChange={(e) => updateTheme({ primaryColor: e.target.value })}
                  style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }}
                />
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={theme.primaryColor || '#5b6af0'}
                  placeholder="#5b6af0"
                  onChange={(e) => updateTheme({ primaryColor: e.target.value })}
                />
              </div>
            </SettingRow>
            <SettingRow label="Цвет фона страниц">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={theme.bgColor || '#ffffff'}
                  onChange={(e) => updateTheme({ bgColor: e.target.value })}
                  style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }}
                />
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={theme.bgColor || '#ffffff'}
                  placeholder="#ffffff"
                  onChange={(e) => updateTheme({ bgColor: e.target.value })}
                />
              </div>
            </SettingRow>
            <SettingRow label="Цвет текста">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={theme.textColor || '#111111'}
                  onChange={(e) => updateTheme({ textColor: e.target.value })}
                  style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }}
                />
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={theme.textColor || '#111111'}
                  placeholder="#111111"
                  onChange={(e) => updateTheme({ textColor: e.target.value })}
                />
              </div>
            </SettingRow>
            <SettingRow label={`Скругление углов: ${theme.borderRadius ?? 8}px`} hint="Влияет на кнопки и карточки по всему сайту">
              <input
                type="range"
                min="0"
                max="24"
                step="1"
                value={theme.borderRadius ?? 8}
                onChange={(e) => updateTheme({ borderRadius: Number(e.target.value) })}
                style={{ width: '100%' }}
              />
            </SettingRow>
            <SettingRow label="Шрифт заголовков" hint="Применяется к h1–h6 по всему сайту">
              <select
                className="prop-input"
                value={theme.fontHeading || ''}
                onChange={(e) => updateTheme({ fontHeading: e.target.value || undefined })}
              >
                <option value="">— системный шрифт —</option>
                {GOOGLE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </SettingRow>
            <SettingRow label="Шрифт основного текста" hint="Применяется к параграфам и body по всему сайту">
              <select
                className="prop-input"
                value={theme.fontBody || ''}
                onChange={(e) => updateTheme({ fontBody: e.target.value || undefined })}
              >
                <option value="">— системный шрифт —</option>
                {GOOGLE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </SettingRow>
            <SettingRow label="Фон карточек" hint="Цвет фона панелей, карточек и поверхностей">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={theme.surface || '#f9fafb'}
                  onChange={(e) => updateTheme({ surface: e.target.value })}
                  style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }}
                />
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={theme.surface || '#f9fafb'}
                  placeholder="#f9fafb"
                  onChange={(e) => updateTheme({ surface: e.target.value })}
                />
              </div>
            </SettingRow>
            <SettingRow label="Приглушённый текст" hint="Цвет второстепенного текста, подписей, плейсхолдеров">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={theme.textMuted || '#6b7280'}
                  onChange={(e) => updateTheme({ textMuted: e.target.value })}
                  style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }}
                />
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={theme.textMuted || '#6b7280'}
                  placeholder="#6b7280"
                  onChange={(e) => updateTheme({ textMuted: e.target.value })}
                />
              </div>
            </SettingRow>
            <SettingRow label="Цвет рамок" hint="Цвет рамок, разделителей и контуров">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={theme.border || '#e5e7eb'}
                  onChange={(e) => updateTheme({ border: e.target.value })}
                  style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }}
                />
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={theme.border || '#e5e7eb'}
                  placeholder="#e5e7eb"
                  onChange={(e) => updateTheme({ border: e.target.value })}
                />
              </div>
            </SettingRow>
            <SettingRow label="CSS-переменные" hint="Глобальные CSS-переменные для :root. Например: --brand: #ff0000; --heading-size: 48px;">
              <textarea
                className="prop-input"
                rows={4}
                placeholder={'--brand: #ff0000;\n--heading-size: 48px;'}
                value={theme.customVars || ''}
                onChange={(e) => updateTheme({ customVars: e.target.value || undefined })}
                style={{ fontFamily: 'monospace', fontSize: '11px', resize: 'vertical' }}
              />
            </SettingRow>
            <SettingRow label="Стиль дизайна" hint="Глобальный визуальный стиль: меняет шрифты, радиус, тени и рамки по всему сайту">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <button
                  onClick={() => updateSettings({ designStyle: '' })}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                    padding: '10px 6px', borderRadius: '8px', cursor: 'pointer',
                    border: !settings.designStyle ? '2px solid var(--accent)' : '1px solid var(--border)',
                    background: !settings.designStyle ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)',
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: '18px' }}>○</span>
                  <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-primary)' }}>Без стиля</span>
                </button>
                {DESIGN_STYLES.map(style => (
                  <button
                    key={style.id}
                    onClick={() => updateSettings({ designStyle: style.id })}
                    title={style.description}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                      padding: '10px 6px', borderRadius: '8px', cursor: 'pointer',
                      border: settings.designStyle === style.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                      background: settings.designStyle === style.id ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ width: '32px', height: '20px', borderRadius: '4px', background: style.previewBg, border: `2px solid ${style.previewAccent}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '10px', color: style.previewAccent }}>{style.emoji}</span>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center', lineHeight: 1.2 }}>{style.label}</span>
                  </button>
                ))}
              </div>
              {settings.designStyle && (() => {
                const active = DESIGN_STYLES.find(s => s.id === settings.designStyle)
                return active ? (
                  <div style={{ marginTop: '8px', padding: '8px 10px', background: 'var(--bg-3)', borderRadius: '6px', fontSize: '11px', color: 'var(--text-muted)' }}>
                    {active.emoji} {active.label} — {active.description}
                  </div>
                ) : null
              })()}
            </SettingRow>
          </SettingsSection>

          {/* AI settings */}
          <SettingsSection title="AI-ассистент" icon="✨">
            <SettingRow label="Режим подтверждения" hint="Когда AI-ассистент запрашивает разрешение перед применением изменений">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {([
                  { value: 'auto', label: 'Авто', desc: 'Применять все изменения сразу без подтверждения' },
                  { value: 'ask_destructive', label: 'Спрашивать при удалении', desc: 'Подтверждать только удаление блоков и смену темы' },
                  { value: 'always_ask', label: 'Всегда спрашивать', desc: 'Показывать превью любых изменений перед применением' },
                ] as const).map(opt => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', background: settings.aiApprovalLevel === opt.value ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)', border: `1px solid ${settings.aiApprovalLevel === opt.value ? 'rgba(91,106,240,0.35)' : 'var(--border)'}`, borderRadius: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="aiApprovalLevel"
                      value={opt.value}
                      checked={settings.aiApprovalLevel === opt.value}
                      onChange={() => updateSettings({ aiApprovalLevel: opt.value })}
                      style={{ marginTop: '2px', flexShrink: 0, accentColor: '#5b6af0' }}
                    />
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{opt.label}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4 }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </SettingRow>
          </SettingsSection>

          {/* Analytics */}
          <SettingsSection title="Аналитика" icon="📊">
            <SettingRow label="Google Analytics 4 ID" hint="GA4 Measurement ID — gtag.js подключается автоматически">
              <input
                type="text"
                className="prop-input"
                value={settings.gaId}
                placeholder="G-XXXXXXXXXX"
                onChange={(e) => updateSettings({ gaId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Яндекс.Метрика ID" hint="Номер счётчика Яндекс.Метрики">
              <input
                type="text"
                className="prop-input"
                value={settings.ymId}
                placeholder="12345678"
                onChange={(e) => updateSettings({ ymId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Facebook Pixel ID" hint="Пиксель Meta для ретаргетинга и конверсий">
              <input
                type="text"
                className="prop-input"
                value={settings.fbPixelId}
                placeholder="123456789012345"
                onChange={(e) => updateSettings({ fbPixelId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="VK Pixel ID" hint="Пиксель ВКонтакте (Retargeting ID)">
              <input
                type="text"
                className="prop-input"
                value={settings.vkPixelId}
                placeholder="VK-RTRG-XXXXXXX-XXXXX"
                onChange={(e) => updateSettings({ vkPixelId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Google Tag Manager ID" hint="GTM-контейнер подключается автоматически — управляйте всеми тегами в GTM">
              <input
                type="text"
                className="prop-input"
                value={settings.gtmId}
                placeholder="GTM-XXXXXXX"
                onChange={(e) => updateSettings({ gtmId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Microsoft Clarity ID" hint="Запись сессий и тепловые карты от Microsoft. Создайте проект на clarity.microsoft.com">
              <input
                type="text"
                className="prop-input"
                value={settings.clarityProjectId}
                placeholder="xxxxxxxxxx"
                onChange={(e) => updateSettings({ clarityProjectId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Hotjar Site ID" hint="Тепловые карты и записи сессий Hotjar. Найдите Site ID в Hotjar → Settings → Tracking Code">
              <input
                type="text"
                className="prop-input"
                value={settings.hotjarSiteId}
                placeholder="1234567"
                onChange={(e) => updateSettings({ hotjarSiteId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Plausible Domain" hint="Приватная аналитика Plausible. Укажите домен сайта так же, как он задан в Plausible">
              <input
                type="text"
                className="prop-input"
                value={settings.plausibleDomain}
                placeholder="example.com"
                onChange={(e) => updateSettings({ plausibleDomain: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Matomo URL" hint="URL вашего Matomo/Piwik сервера. Например: https://analytics.example.com">
              <input
                type="url"
                className="prop-input"
                value={settings.matomoUrl}
                placeholder="https://analytics.example.com"
                onChange={(e) => updateSettings({ matomoUrl: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Matomo Site ID" hint="ID сайта в Matomo (Администрирование → Сайты → ID)">
              <input
                type="text"
                className="prop-input"
                value={settings.matomoSiteId}
                placeholder="1"
                onChange={(e) => updateSettings({ matomoSiteId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Google верификация" hint="Код из Google Search Console → Настройки → Подтверждение права собственности → HTML-тег">
              <input
                type="text"
                className="prop-input"
                value={settings.googleVerification}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                onChange={(e) => updateSettings({ googleVerification: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Яндекс верификация" hint="Код из Яндекс Вебмастер → Подтверждение прав → meta-тег (только значение content)">
              <input
                type="text"
                className="prop-input"
                value={settings.yandexVerification}
                placeholder="xxxxxxxxxxxxxxxx"
                onChange={(e) => updateSettings({ yandexVerification: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Bing верификация" hint="Код из Bing Webmaster Tools → Подтверждение сайта → HTML-тег meta">
              <input
                type="text"
                className="prop-input"
                value={settings.bingVerification}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                onChange={(e) => updateSettings({ bingVerification: e.target.value.trim() })}
              />
            </SettingRow>
          </SettingsSection>

          {/* Domain */}
          <SettingsSection title="Домен" icon="🌐">
            {/* Subdomain (always available) */}
            {(() => {
              const siteSlug = localStorage.getItem('bst-site-slug') || settings.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'my-site'
              return (
                <div style={{ marginBottom: '12px', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>Субдомен (всегда активен)</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{siteSlug}.mybst.ru</div>
                  </div>
                  <span style={{ fontSize: '10px', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>SSL ✓</span>
                </div>
              )
            })()}

            {/* Custom domain */}
            <SettingRow
              label="Кастомный домен"
              hint={userPlan === 'FREE' ? 'Доступно с тарифа Personal' : 'Укажите домен и добавьте CNAME у регистратора'}
            >
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="prop-input"
                  style={{ flex: 1 }}
                  value={settings.customDomain}
                  placeholder={userPlan === 'FREE' ? 'Недоступно на Free тарифе' : 'mysite.com'}
                  disabled={userPlan === 'FREE'}
                  onChange={(e) => updateSettings({ customDomain: e.target.value.trim(), customDomainVerified: false })}
                />
                {settings.customDomain && (
                  <button
                    onClick={async () => {
                      if (!siteId) return
                      try { await sitesApi.removeDomain(siteId); updateSettings({ customDomain: '', customDomainVerified: false }) } catch { /* ignore */ }
                    }}
                    style={{ padding: '5px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                  >
                    Удалить
                  </button>
                )}
              </div>
              {settings.customDomainVerified && settings.customDomain && (
                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>✓ Домен подтверждён</span>
                </div>
              )}
            </SettingRow>

            {settings.customDomain && userPlan !== 'FREE' && (
              <div style={{ marginTop: '4px', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Инструкция по подключению:</div>
                <div>Добавьте CNAME-запись у вашего регистратора домена:</div>
                <code style={{ display: 'block', fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '6px 8px', borderRadius: '4px', marginTop: '6px', fontSize: '11px' }}>
                  {settings.customDomain} → cname.vercel-dns.com
                </code>
                <div style={{ marginTop: '6px', color: 'var(--text-muted)' }}>Распространение DNS занимает до 48 часов</div>
              </div>
            )}

            {settings.customDomain && userPlan !== 'FREE' && (
              <DomainVerifier siteId={siteId} domain={settings.customDomain} />
            )}

            {userPlan === 'FREE' && !settings.customDomain && (
              <div style={{ marginTop: '8px', padding: '10px 12px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px', color: '#92400e', border: '1px solid #fcd34d' }}>
                🔒 Кастомные домены доступны с тарифа <strong>Personal</strong>. Субдомен mybst.ru включён бесплатно.
              </div>
            )}
          </SettingsSection>

          {/* Forms & Notifications */}
          <SettingsSection title="Формы и уведомления" icon="📬">
            <SettingRow label="Email для уведомлений о заявках" hint="На этот адрес будут приходить письма при отправке форм">
              <input
                type="email"
                className="prop-input"
                value={settings.formEmailTo}
                placeholder="you@example.com"
                onChange={(e) => updateSettings({ formEmailTo: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Webhook URL" hint="POST-запрос с данными формы. Заголовок X-BST-Event: form.submit">
              <input
                type="url"
                className="prop-input"
                value={settings.formWebhookUrl}
                placeholder="https://hooks.example.com/bst"
                onChange={(e) => updateSettings({ formWebhookUrl: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Telegram Chat ID" hint="Отправьте /start боту @bst_notify_bot чтобы узнать ваш chat ID. Уведомления придут при каждой отправке формы.">
              <input
                type="text"
                className="prop-input"
                value={settings.formTelegramChatId ?? ''}
                placeholder="-100123456789"
                onChange={(e) => updateSettings({ formTelegramChatId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Битрикс24 Webhook" hint="Лиды с форм автоматически создаются в CRM. В Битрикс24 → Приложения → Вебхуки → Входящий вебхук → crm.lead.add.">
              <input
                type="url"
                className="prop-input"
                value={settings.bitrix24WebhookUrl ?? ''}
                placeholder="https://domain.bitrix24.ru/rest/1/token/"
                onChange={(e) => updateSettings({ bitrix24WebhookUrl: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="amoCRM — домен" hint="Поддомен вашего amoCRM: например 'mycompany' для mycompany.amocrm.ru">
              <input
                type="text"
                className="prop-input"
                value={settings.amoCrmDomain ?? ''}
                placeholder="mycompany"
                onChange={(e) => updateSettings({ amoCrmDomain: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="amoCRM — API токен" hint="Долгосрочный токен интеграции amoCRM. Заявки с форм создаются как сделки.">
              <input
                type="password"
                className="prop-input"
                value={settings.amoCrmToken ?? ''}
                placeholder="Токен интеграции"
                onChange={(e) => updateSettings({ amoCrmToken: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="GetCourse — домен" hint="Домен вашей школы GetCourse. Заявки с форм отправляются как лиды. Пример: myschool.getcourse.ru">
              <input
                type="text"
                className="prop-input"
                value={settings.getCourseDomain ?? ''}
                placeholder="myschool.getcourse.ru"
                onChange={(e) => updateSettings({ getCourseDomain: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="SMS.ru API-ключ" hint="При получении заявки владелец сайта получает SMS-уведомление. Зарегистрируйтесь на sms.ru.">
              <input
                type="password"
                className="prop-input"
                value={settings.smsRuApiKey ?? ''}
                placeholder="Ключ из личного кабинета sms.ru"
                onChange={(e) => updateSettings({ smsRuApiKey: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="SMS.ru — номер для уведомлений" hint="На этот номер будут приходить SMS о новых заявках. Формат: 79161234567">
              <input
                type="tel"
                className="prop-input"
                value={settings.smsOwnerPhone ?? ''}
                placeholder="79161234567"
                onChange={(e) => updateSettings({ smsOwnerPhone: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="reCAPTCHA v3 Site Key" hint="Защита форм от спам-ботов. Создайте ключи на Google reCAPTCHA Admin Console (v3). Укажите оба ключа.">
              <input
                type="text"
                className="prop-input"
                value={settings.recaptchaSiteKey ?? ''}
                placeholder="6LeXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                onChange={(e) => updateSettings({ recaptchaSiteKey: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="reCAPTCHA v3 Secret Key" hint="Секретный ключ с сервера Google reCAPTCHA. Хранится безопасно на сервере.">
              <input
                type="password"
                className="prop-input"
                value={settings.recaptchaSecretKey ?? ''}
                placeholder="6LeXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                onChange={(e) => updateSettings({ recaptchaSecretKey: e.target.value.trim() })}
              />
            </SettingRow>
          </SettingsSection>

          {/* Email marketing */}
          <SettingsSection title="Email-маркетинг" icon="📧">
            <SettingRow label="Mailchimp API Key" hint="Подписчики из форм автоматически добавляются в список">
              <input
                type="password"
                className="prop-input"
                value={settings.mailchimpApiKey ?? ''}
                placeholder="xxxxxxxx-us1"
                onChange={(e) => updateSettings({ mailchimpApiKey: e.target.value.trim() })}
                autoComplete="off"
              />
            </SettingRow>
            <SettingRow label="Mailchimp List (Audience) ID" hint="ID аудитории в Mailchimp (в настройках списка → Audience ID)">
              <input
                type="text"
                className="prop-input"
                value={settings.mailchimpListId ?? ''}
                placeholder="a1b2c3d4e5"
                onChange={(e) => updateSettings({ mailchimpListId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Unisender API Key" hint="Email-адреса из форм добавляются в список Unisender">
              <input
                type="password"
                className="prop-input"
                value={settings.unisenderApiKey ?? ''}
                placeholder="API key"
                onChange={(e) => updateSettings({ unisenderApiKey: e.target.value.trim() })}
                autoComplete="off"
              />
            </SettingRow>
            <SettingRow label="Unisender List ID" hint="ID списка в Unisender (Контакты → Списки контактов)">
              <input
                type="text"
                className="prop-input"
                value={settings.unisenderListId ?? ''}
                placeholder="12345"
                onChange={(e) => updateSettings({ unisenderListId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="SendPulse Client ID" hint="OAuth клиент из SendPulse API → Настройки → API">
              <input
                type="text"
                className="prop-input"
                value={settings.sendpulseClientId ?? ''}
                placeholder="client_id"
                onChange={(e) => updateSettings({ sendpulseClientId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="SendPulse Client Secret" hint="OAuth секрет из SendPulse API → Настройки → API">
              <input
                type="password"
                className="prop-input"
                value={settings.sendpulseClientSecret ?? ''}
                placeholder="client_secret"
                onChange={(e) => updateSettings({ sendpulseClientSecret: e.target.value.trim() })}
                autoComplete="off"
              />
            </SettingRow>
            <SettingRow label="SendPulse Address Book ID" hint="ID адресной книги SendPulse (Email → Адресные книги → ID в URL)">
              <input
                type="text"
                className="prop-input"
                value={settings.sendpulseListId ?? ''}
                placeholder="1234567"
                onChange={(e) => updateSettings({ sendpulseListId: e.target.value.trim() })}
              />
            </SettingRow>
          </SettingsSection>

          {/* Workspace / Agency */}
          <SettingsSection title="Рабочее пространство" icon="🏢">
            <WorkspaceSection userPlan={userPlan} />
          </SettingsSection>

          {/* Webhooks */}
          <SettingsSection title="Вебхуки (исходящие)" icon="🔗">
            <WebhooksSection siteId={siteId} />
          </SettingsSection>

          {/* Inbound Webhooks */}
          <SettingsSection title="Входящие вебхуки (Zapier / Make)" icon="📥">
            <InboundWebhooksSection siteId={siteId} />
          </SettingsSection>

          {/* API Keys */}
          <SettingsSection title="API ключи" icon="🔑">
            <ApiKeysSection />
          </SettingsSection>

          {/* Redirects */}
          <SettingsSection title="Редиректы" icon="↪">
            <RedirectsSection siteId={siteId} />
          </SettingsSection>

          {/* Cookie Consent */}
          <SettingsSection title="Cookie-уведомление" icon="🍪">
            <SettingRow label="Показывать баннер о cookies" hint="Требуется для сайтов с аналитикой (GA, YM) по 152-ФЗ и GDPR">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.cookieConsent}
                  onChange={(e) => updateSettings({ cookieConsent: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Включить cookie-баннер</span>
              </label>
            </SettingRow>
            {settings.cookieConsent && (
              <SettingRow label="Текст уведомления" hint="Оставьте пустым для текста по умолчанию">
                <input
                  type="text"
                  className="prop-input"
                  value={settings.cookieConsentText}
                  placeholder="Мы используем cookie для улучшения сайта. Продолжая, вы соглашаетесь с политикой."
                  onChange={(e) => updateSettings({ cookieConsentText: e.target.value })}
                />
              </SettingRow>
            )}
          </SettingsSection>

          {/* Social Proof */}
          <SettingsSection title="Социальное доказательство" icon="🔔">
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Всплывающие уведомления в левом нижнем углу — «Иван из Москвы только что оставил заявку». Повышают конверсию.
            </p>
            <SettingRow label="Включить уведомления" hint="Показывать уведомления посетителям сайта">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.socialProofEnabled}
                  onChange={(e) => updateSettings({ socialProofEnabled: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Включить социальное доказательство</span>
              </label>
            </SettingRow>
            {settings.socialProofEnabled && (
              <SettingRow label="Сообщения" hint="Каждое сообщение с новой строки. Используйте {name}, {city} — они заменятся случайными значениями.">
                <textarea
                  className="prop-textarea"
                  rows={5}
                  value={settings.socialProofMessages}
                  placeholder={'Иван из {city} только что оставил заявку\nМария из {city} купила продукт\n{name} из {city} подписался на рассылку'}
                  onChange={(e) => updateSettings({ socialProofMessages: e.target.value })}
                />
              </SettingRow>
            )}
          </SettingsSection>

          {/* Back to Top */}
          <SettingsSection title="Кнопка «Наверх»" icon="⬆">
            <SettingRow label="Показывать кнопку" hint="Кнопка появляется при прокрутке вниз и возвращает в начало страницы">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.backToTop}
                  onChange={(e) => updateSettings({ backToTop: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Включить кнопку «Наверх»</span>
              </label>
            </SettingRow>
          </SettingsSection>

          {/* Scroll Progress */}
          <SettingsSection title="Прогресс-бар прокрутки" icon="📊">
            <SettingRow label="Показывать прогресс" hint="Тонкая цветная полоска вверху страницы, показывающая прогресс прокрутки">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.scrollProgress}
                  onChange={(e) => updateSettings({ scrollProgress: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Включить прогресс-бар</span>
              </label>
            </SettingRow>
            {settings.scrollProgress && (
              <SettingRow label="Цвет полоски" hint="HEX-цвет прогресс-бара">
                <input
                  type="color"
                  value={settings.scrollProgressColor || '#5b6af0'}
                  onChange={(e) => updateSettings({ scrollProgressColor: e.target.value })}
                  style={{ width: '48px', height: '32px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', padding: '2px' }}
                />
              </SettingRow>
            )}
          </SettingsSection>

          {/* Page Preloader */}
          <SettingsSection title="Прелоадер страницы" icon="⏳">
            <SettingRow label="Показывать загрузку" hint="Экран загрузки перед появлением контента — улучшает восприятие скорости сайта">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.pagePreloader}
                  onChange={(e) => updateSettings({ pagePreloader: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Включить прелоадер</span>
              </label>
            </SettingRow>
          </SettingsSection>

          {/* Custom Cursor */}
          <SettingsSection title="Кастомный курсор" icon="🖱">
            <SettingRow label="Включить кастомный курсор" hint="Заменяет стандартный курсор браузера на анимированный брендовый">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.customCursor}
                  onChange={(e) => updateSettings({ customCursor: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Включить</span>
              </label>
            </SettingRow>
            {settings.customCursor && (
              <SettingRow label="Цвет курсора">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={settings.customCursorColor || '#5b6af0'}
                    onChange={(e) => updateSettings({ customCursorColor: e.target.value })}
                    style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }}
                  />
                  <input
                    type="text"
                    className="prop-input"
                    style={{ flex: 1 }}
                    value={settings.customCursorColor || '#5b6af0'}
                    placeholder="#5b6af0"
                    onChange={(e) => updateSettings({ customCursorColor: e.target.value })}
                  />
                </div>
              </SettingRow>
            )}
          </SettingsSection>

          {/* Page Transition */}
          <SettingsSection title="Переход между страницами" icon="✨">
            <SettingRow label="Анимация перехода" hint="Плавный переход при клике по ссылкам на сайте. Улучшает ощущение навигации.">
              <select
                className="prop-input"
                value={settings.pageTransition || ''}
                onChange={(e) => updateSettings({ pageTransition: e.target.value })}
              >
                <option value="">— отключено —</option>
                <option value="fade">Fade — плавное появление</option>
                <option value="slide-up">Slide Up — снизу вверх</option>
                <option value="slide-left">Slide Left — справа налево</option>
                <option value="zoom">Zoom — масштабирование</option>
              </select>
            </SettingRow>
          </SettingsSection>

          {/* Site Language */}
          <SettingsSection title="Язык сайта" icon="🌐">
            <SettingRow label="Основной язык" hint="Используется в теге <html lang='...'> и hreflang. Влияет на SEO и доступность.">
              <select
                className="prop-input"
                value={settings.siteLanguage || ''}
                onChange={(e) => updateSettings({ siteLanguage: e.target.value })}
              >
                <option value="">— не задан —</option>
                <option value="ru">Русский (ru)</option>
                <option value="en">English (en)</option>
                <option value="uk">Українська (uk)</option>
                <option value="kk">Қазақша (kk)</option>
                <option value="be">Беларуская (be)</option>
                <option value="de">Deutsch (de)</option>
                <option value="fr">Français (fr)</option>
                <option value="es">Español (es)</option>
                <option value="it">Italiano (it)</option>
                <option value="zh">中文 (zh)</option>
                <option value="ar">العربية (ar)</option>
              </select>
            </SettingRow>
          </SettingsSection>

          {/* Multilingual */}
          <SettingsSection title="Мультиязычность" icon="🗺">
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Добавьте дополнительные языки. Переводы создаются через AI на вкладке «Переводы» в панели страницы. Доступны по URL <code style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: '3px' }}>?lang=en</code>
            </p>
            {([
              { code: 'en', name: 'English', flag: '🇺🇸' },
              { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
              { code: 'fr', name: 'Français', flag: '🇫🇷' },
              { code: 'es', name: 'Español', flag: '🇪🇸' },
              { code: 'it', name: 'Italiano', flag: '🇮🇹' },
              { code: 'pt', name: 'Português', flag: '🇵🇹' },
              { code: 'pl', name: 'Polski', flag: '🇵🇱' },
              { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
              { code: 'ar', name: 'العربية', flag: '🇸🇦' },
              { code: 'zh', name: '中文', flag: '🇨🇳' },
              { code: 'ja', name: '日本語', flag: '🇯🇵' },
              { code: 'ko', name: '한국어', flag: '🇰🇷' },
              { code: 'uk', name: 'Українська', flag: '🇺🇦' },
              { code: 'kk', name: 'Қазақша', flag: '🇰🇿' },
              { code: 'uz', name: "O'zbek", flag: '🇺🇿' },
            ] as const).map(lang => {
              const enabled = (settings.additionalLanguages ?? []).includes(lang.code)
              return (
                <label key={lang.code} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)' }}>
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => {
                      const current = settings.additionalLanguages ?? []
                      updateSettings({
                        additionalLanguages: e.target.checked
                          ? [...current, lang.code]
                          : current.filter(c => c !== lang.code),
                      })
                    }}
                  />
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                  {enabled && <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--accent)', fontWeight: 600 }}>✓ включён</span>}
                </label>
              )
            })}
          </SettingsSection>

          {/* Messenger Buttons */}
          <SettingsSection title="Кнопки мессенджеров" icon="💬">
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Плавающие кнопки в правом нижнем углу сайта. Заполните нужные — остальные не появятся.
            </p>
            <SettingRow label="WhatsApp номер" hint="Номер в международном формате: +79991234567">
              <input
                type="text"
                className="prop-input"
                value={settings.whatsappNumber ?? ''}
                placeholder="+79991234567"
                onChange={(e) => updateSettings({ whatsappNumber: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Telegram username" hint="Username без @, например: mycompany">
              <input
                type="text"
                className="prop-input"
                value={settings.telegramUsername ?? ''}
                placeholder="mycompany"
                onChange={(e) => updateSettings({ telegramUsername: e.target.value.replace('@', '').trim() })}
              />
            </SettingRow>
            <SettingRow label="ВКонтакте ID группы" hint="ID или короткий адрес группы/паблика, например: mygroup">
              <input
                type="text"
                className="prop-input"
                value={settings.vkGroupId ?? ''}
                placeholder="mygroup"
                onChange={(e) => updateSettings({ vkGroupId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Телефон (звонок)" hint="Кнопка звонка в правом нижнем углу: +79991234567">
              <input
                type="tel"
                className="prop-input"
                value={settings.callbackPhone ?? ''}
                placeholder="+79991234567"
                onChange={(e) => updateSettings({ callbackPhone: e.target.value.trim() })}
              />
            </SettingRow>
          </SettingsSection>

          {/* Live Chat Widgets */}
          <SettingsSection title="Онлайн-чат" icon="🗨">
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Подключите онлайн-чат к сайту. Заполните только один из виджетов — они взаимно исключают друг друга.
            </p>
            <SettingRow label="JivoChat ID" hint="Код виджета из личного кабинета JivoChat (например: AbCd1234Ef)">
              <input
                type="text"
                className="prop-input"
                value={settings.jivoChatId ?? ''}
                placeholder="AbCd1234Ef"
                onChange={(e) => updateSettings({ jivoChatId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Crisp Website ID" hint="Website ID из настроек Crisp (Integrations → Chat)">
              <input
                type="text"
                className="prop-input"
                value={settings.crispWebsiteId ?? ''}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                onChange={(e) => updateSettings({ crispWebsiteId: e.target.value.trim() })}
              />
            </SettingRow>
            <SettingRow label="Tawk.to Property ID" hint="Property ID из настроек Tawk.to (например: 6123ab...)">
              <input
                type="text"
                className="prop-input"
                value={settings.tawkPropertyId ?? ''}
                placeholder="6123abcd1234567890"
                onChange={(e) => updateSettings({ tawkPropertyId: e.target.value.trim() })}
              />
            </SettingRow>
            {settings.tawkPropertyId && (
              <SettingRow label="Tawk.to Widget ID" hint="Widget ID из URL виджета (обычно: 'default' или хэш)">
                <input
                  type="text"
                  className="prop-input"
                  value={settings.tawkWidgetId ?? ''}
                  placeholder="default"
                  onChange={(e) => updateSettings({ tawkWidgetId: e.target.value.trim() })}
                />
              </SettingRow>
            )}
          </SettingsSection>

          {/* Custom Code */}
          <SettingsSection title="Свой HTML-код в <head>" icon="</>">
            <SettingRow label="Custom HTML" hint="Произвольный код для вставки в <head> (теги, скрипты, стили)">
              <textarea
                className="prop-textarea"
                rows={5}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                value={settings.customHeadHtml}
                placeholder={'<!-- Ваш код -->\n<meta name="..." content="...">'}
                onChange={(e) => updateSettings({ customHeadHtml: e.target.value })}
              />
            </SettingRow>
          </SettingsSection>

          {/* Custom Body Code */}
          <SettingsSection title="Код перед </body> — чат-виджеты" icon="💬">
            <SettingRow label="Custom HTML в конце <body>" hint="Вставьте сюда код чат-виджета (JivoChat, Crisp, Tawk.to, WhatsApp button и т.д.)">
              <textarea
                className="prop-textarea"
                rows={6}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                value={settings.customBodyHtml ?? ''}
                placeholder={'<!-- JivoChat, Crisp, Tawk.to, WhatsApp Button... -->\n<script src="//code.jivosite.com/widget/..."></script>'}
                onChange={(e) => updateSettings({ customBodyHtml: e.target.value })}
              />
            </SettingRow>
          </SettingsSection>

          {/* PWA Settings */}
          <SettingsSection title="PWA — Прогрессивное приложение" icon="📲">
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Позволяет посетителям устанавливать ваш сайт как приложение на телефоне. Требует иконку сайта (favicon).
            </p>
            <SettingRow label="Включить PWA">
              <input
                type="checkbox"
                checked={settings.pwaEnabled}
                onChange={(e) => updateSettings({ pwaEnabled: e.target.checked })}
              />
            </SettingRow>
            {settings.pwaEnabled && (<>
              <SettingRow label="Название приложения" hint="Полное имя (до 45 символов)">
                <input
                  type="text"
                  className="prop-input"
                  value={settings.pwaName}
                  placeholder={settings.name || 'Моё приложение'}
                  onChange={(e) => updateSettings({ pwaName: e.target.value })}
                />
              </SettingRow>
              <SettingRow label="Краткое название" hint="Отображается на иконке (до 12 символов)">
                <input
                  type="text"
                  className="prop-input"
                  value={settings.pwaShortName}
                  placeholder={settings.name?.slice(0, 12) || 'Сайт'}
                  onChange={(e) => updateSettings({ pwaShortName: e.target.value })}
                />
              </SettingRow>
              <SettingRow label="Цвет темы" hint="Цвет панели браузера (на Android)">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="color" value={settings.pwaThemeColor || '#5b6af0'} onChange={(e) => updateSettings({ pwaThemeColor: e.target.value })} style={{ width: '36px', height: '28px', padding: '1px 2px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer' }} />
                  <input type="text" className="prop-input" style={{ flex: 1 }} value={settings.pwaThemeColor || '#5b6af0'} onChange={(e) => updateSettings({ pwaThemeColor: e.target.value })} />
                </div>
              </SettingRow>
              <SettingRow label="Фон экрана загрузки" hint="Цвет сплэш-экрана при открытии приложения">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="color" value={settings.pwaBackgroundColor || '#ffffff'} onChange={(e) => updateSettings({ pwaBackgroundColor: e.target.value })} style={{ width: '36px', height: '28px', padding: '1px 2px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer' }} />
                  <input type="text" className="prop-input" style={{ flex: 1 }} value={settings.pwaBackgroundColor || '#ffffff'} onChange={(e) => updateSettings({ pwaBackgroundColor: e.target.value })} />
                </div>
              </SettingRow>
            </>)}
          </SettingsSection>

          {/* Maintenance Mode */}
          <SettingsSection title="Режим обслуживания" icon="🔧">
            <SettingRow label="Включить режим обслуживания" hint="Сайт будет показывать заглушку «Coming soon» всем посетителям">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.maintenanceMode ?? false}
                  onChange={(e) => updateSettings({ maintenanceMode: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: (settings.maintenanceMode) ? '#ef4444' : 'var(--text-secondary)' }}>
                  {settings.maintenanceMode ? '⚠ Сайт скрыт от посетителей' : 'Выключено'}
                </span>
              </label>
            </SettingRow>
            {settings.maintenanceMode && (
              <SettingRow label="Текст заглушки" hint="Сообщение, которое увидят посетители сайта">
                <input
                  type="text"
                  className="prop-input"
                  value={settings.maintenanceText ?? ''}
                  placeholder="Сайт временно недоступен. Скоро вернёмся!"
                  onChange={(e) => updateSettings({ maintenanceText: e.target.value })}
                />
              </SettingRow>
            )}
          </SettingsSection>

          {/* Public Gallery */}
          <SettingsSection title="Публичная галерея" icon="🌍">
            <SettingRow label="Показать в галерее сообщества" hint="Другие пользователи BST смогут увидеть ваш сайт в разделе «Вдохновение» на дашборде.">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.publiclyListed ?? false}
                  onChange={(e) => updateSettings({ publiclyListed: e.target.checked })}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Включить публичный листинг</span>
              </label>
            </SettingRow>
            {settings.publiclyListed && (
              <SettingRow label="Теги" hint="Через запятую: portfolio, landing, shop, blog, etc.">
                <input
                  type="text"
                  className="prop-input"
                  value={(settings.publicListingTags ?? []).join(', ')}
                  placeholder="portfolio, landing, shop"
                  onChange={(e) => {
                    const tags = e.target.value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
                    updateSettings({ publicListingTags: tags })
                  }}
                />
              </SettingRow>
            )}
          </SettingsSection>

          {/* Site Password */}
          {siteId && (
            <SettingsSection title="Пароль на сайт" icon="🔑">
              <SitePasswordSection siteId={siteId} />
            </SettingsSection>
          )}

          {/* Newsletter subscribers */}
          {siteId && (
            <SettingsSection title="Подписчики рассылки" icon="📩">
              <NewsletterSection siteId={siteId} />
            </SettingsSection>
          )}

          {/* Weekly Reports */}
          <SettingsSection title="Email-отчёты" icon="📊">
            <WeeklyReportsSection />
          </SettingsSection>

          {/* Marketing consent (152-ФЗ + ФЗ «О рекламе») */}
          <SettingsSection title="Маркетинговые уведомления" icon="📣">
            <MarketingConsentSection />
          </SettingsSection>

          {/* §96 Save as Template */}
          {siteId && (
            <SettingsSection title="Шаблон" icon="📦">
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                Сделайте этот сайт шаблоном. Другие пользователи смогут использовать его как основу для своих проектов (§96).
              </div>
              <button
                onClick={() => setSaveTemplateOpen(true)}
                style={{ padding: '9px 18px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', cursor: 'pointer' }}
              >
                📦 Сохранить как шаблон
              </button>
            </SettingsSection>
          )}

          {/* Site Transfer */}
          {siteId && (
            <SettingsSection title="Передача сайта" icon="🔁">
              <SiteTransferSection siteId={siteId} />
            </SettingsSection>
          )}

          {/* BST Badge */}
          <SettingsSection title="Бейдж BST" icon="B">
            <SettingRow label="Скрыть бейдж «Сделано на BST»" hint="Бейдж отображается в нижней части сайта.">
              {['AGENCY', 'ENTERPRISE'].includes(userPlan) ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings.hideBstBadge ?? false}
                    onChange={(e) => updateSettings({ hideBstBadge: e.target.checked })}
                  />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Скрыть бейдж</span>
                </label>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'not-allowed', opacity: 0.4 }}>
                    <input type="checkbox" disabled checked={false} />
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Скрыть бейдж</span>
                  </label>
                  <span style={{ fontSize: '10px', fontWeight: 700, background: '#f59e0b22', color: '#f59e0b', padding: '2px 8px', borderRadius: '8px', whiteSpace: 'nowrap' }}>Agency+</span>
                </div>
              )}
            </SettingRow>
          </SettingsSection>

          {/* Preview */}
          {(settings.gaId || settings.ymId || settings.favicon || settings.customHeadHtml) && (
            <SettingsSection title="Что попадёт в <head>" icon="✓">
              <div style={{ background: 'var(--bg-3)', borderRadius: '8px', padding: '12px 14px', fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                {settings.favicon ? `<link rel="icon" href="${settings.favicon}">\n` : ''}
                {settings.name ? `<meta property="og:site_name" content="${settings.name}">\n` : ''}
                {settings.gaId ? `<!-- Google Analytics 4 -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=${settings.gaId}"></script>\n` : ''}
                {settings.ymId ? `<!-- Яндекс.Метрика -->\n<script>/* ym(${settings.ymId}, "init", {...}) */</script>\n` : ''}
                {settings.customHeadHtml ? `\n<!-- Custom -->\n${settings.customHeadHtml}` : ''}
              </div>
            </SettingsSection>
          )}

          {/* Опасная зона (152-ФЗ ст.14 ч.1 п.7) */}
          <SettingsSection title="Опасная зона" icon="⚠️">
            <DeleteAccountSection />
          </SettingsSection>

        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button
            onClick={() => setSettingsOpen(false)}
            style={{ padding: '9px 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '14px', cursor: 'pointer' }}
          >
            Сохранить
          </button>
        </div>
      </div>

      {saveTemplateOpen && siteId && (
        <SaveAsTemplateModal
          siteId={siteId}
          siteName={settings.name ?? 'Мой сайт'}
          onClose={() => setSaveTemplateOpen(false)}
        />
      )}
    </div>
  )
}

function SettingsSection({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <span style={{ fontSize: '16px' }}>{icon}</span>
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {children}
      </div>
    </div>
  )
}

function SettingRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '6px' }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>{hint}</div>}
    </div>
  )
}
